import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertCircle,
    AlertTriangle,
    CalendarClock,
    CheckCircle2,
    ChevronRight,
    Loader2,
    MapPin,
    Plus,
} from "lucide-react";

import { ApiError, api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { usePersistedSort } from "@/lib/use-persisted-sort";
import { Dialog } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatCard } from "@/components/ui/stat-card";
import { cn } from "@/lib/utils";
import {
    bucketStyle,
    buildRosterParams,
    daysUntilLabel,
    formatAddressOneLine,
    labelForAssetType,
    type RosterFilters,
    type RosterSite,
} from "./rosterHelpers";

// Reasonable default windows for the office; can refine later from
// real usage. 60-day default mirrors the server.
const DAYS_AHEAD_OPTIONS = [
    { value: "30", label: "30 days" },
    { value: "60", label: "60 days" },
    { value: "90", label: "90 days" },
    { value: "180", label: "6 months" },
    { value: "365", label: "1 year" },
];

const ASSET_TYPE_OPTIONS = [
    { value: "", label: "All asset types" },
    { value: "backflow", label: "Backflow" },
    { value: "extinguisher", label: "Extinguisher" },
    { value: "e_light", label: "Emergency light" },
    { value: "hood", label: "Hood" },
    { value: "hydrant", label: "Hydrant" },
];

const PAGE_SIZE = 50;
/** Must match TEST_DUE_ROSTER_SORT on the server (pagedListSorts.ts). */
const ROSTER_SORT_KEYS = ["due", "customer", "site", "assets", "provider"] as const;

interface RosterResponse {
    sites: RosterSite[];
    total: number;
    limit: number;
    offset: number;
    filters: RosterFilters;
}

interface VisitResponse {
    jobId: string;
    assetCount: number;
    title: string;
}

export default function TestDueRosterPage() {
    const queryClient = useQueryClient();

    // Filter state — kept in component state rather than the URL for
    // now to keep this MVP simple. We can promote to URLSearchParams
    // later once we know which combos are useful for sharing.
    const [daysAhead, setDaysAhead] = useState(60);
    const [includeOverdue, setIncludeOverdue] = useState(true);
    const [hideAlreadyQueued, setHideAlreadyQueued] = useState(true);
    const [assetType, setAssetType] = useState<string>("");
    const [page, setPage] = useState(1);

    const offset = (page - 1) * PAGE_SIZE;
    const filterArgs = {
        daysAhead,
        includeOverdue,
        hideAlreadyQueued,
        assetType: assetType || null,
        limit: PAGE_SIZE,
        offset,
    };

    // Sorted on the server — the roster is paged (faster paths 7h). The
    // default is the old order: earliest due first.
    const sort = usePersistedSort({
        tableId: "scheduling.test-due-roster",
        defaultKey: "due",
        defaultDir: "asc",
        validKeys: ROSTER_SORT_KEYS,
    });

    const roster = useQuery({
        queryKey: ["scheduling", "test-due-roster", filterArgs, sort.sortKey, sort.sortDir],
        // Wait for the saved sort so the roster doesn't load twice.
        enabled: sort.loaded,
        queryFn: () => {
            const params = buildRosterParams(filterArgs);
            params.set("sort", sort.sortKey ?? "due");
            params.set("dir", sort.sortDir);
            return api.get<RosterResponse>(`/api/scheduling/test-due-roster?${params}`);
        },
        // Keep the previous response visible while filters change so the
        // table doesn't pop to "loading" on every checkbox click.
        placeholderData: (prev) => prev,
    });

    const totalSites = roster.data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalSites / PAGE_SIZE));

    const sites = roster.data?.sites ?? [];

    // Derived stats for the cards at the top.
    const overdueSites = useMemo(() => sites.filter((s) => s.due_bucket === "overdue").length, [sites]);
    const dueNowSites = useMemo(() => sites.filter((s) => s.due_bucket === "due_now").length, [sites]);
    const totalAssetsOnPage = useMemo(() => sites.reduce((sum, s) => sum + s.asset_count, 0), [sites]);

    // ── Schedule-visit dialog state ─────────────────────────────────────

    const [visitTarget, setVisitTarget] = useState<RosterSite | null>(null);
    const [visitNotes, setVisitNotes] = useState("");
    const [visitTargetDate, setVisitTargetDate] = useState("");

    const openSchedule = (site: RosterSite) => {
        setVisitTarget(site);
        setVisitNotes("");
        setVisitTargetDate("");
    };
    const closeSchedule = () => setVisitTarget(null);

    const visitMutation = useMutation({
        mutationFn: (body: { siteId: string; notes?: string; targetDate?: string; bundleWindowDays: number }) =>
            api.post<VisitResponse>("/api/scheduling/test-due-visits", body),
        onSuccess: (result) => {
            toast.success(`Queued: ${result.title}`);
            closeSchedule();
            queryClient.invalidateQueries({ queryKey: ["scheduling", "test-due-roster"] });
        },
        onError: (err) => {
            const msg = err instanceof ApiError ? extractErrorMessage(err) : "Could not queue the visit.";
            toast.error(msg);
        },
    });

    const submitVisit = () => {
        if (!visitTarget) return;
        const body: {
            siteId: string;
            notes?: string;
            targetDate?: string;
            bundleWindowDays: number;
        } = {
            siteId: visitTarget.site_id,
            bundleWindowDays: daysAhead,
        };
        if (visitNotes.trim()) body.notes = visitNotes.trim();
        if (visitTargetDate) body.targetDate = visitTargetDate;
        visitMutation.mutate(body);
    };

    // ── Table columns ───────────────────────────────────────────────────

    const today = useMemo(() => new Date(), []);

    const columns: Column<RosterSite>[] = [
        {
            key: "due",
            header: "Status",
            sortable: true,
            render: (s) => {
                const style = bucketStyle(s.due_bucket);
                return (
                    <div className="flex flex-col gap-1">
                        <Badge variant="outline" className={cn("w-fit text-xs", style.badge)}>
                            {style.label}
                        </Badge>
                        <span className="text-xs text-foreground/80">{daysUntilLabel(s.earliest_due_date, today)}</span>
                    </div>
                );
            },
        },
        {
            key: "customer",
            header: "Customer",
            sortable: true,
            render: (s) => (
                <Link to={`/customers/${s.customer_id}`} className="font-medium hover:underline">
                    {s.customer_name}
                </Link>
            ),
        },
        {
            key: "site",
            header: "Site",
            sortable: true,
            render: (s) => {
                const addr = formatAddressOneLine(s);
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-medium">{s.site_name ?? "—"}</span>
                        {addr ? (
                            <span className="text-xs text-foreground/80 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {addr}
                            </span>
                        ) : null}
                    </div>
                );
            },
        },
        {
            key: "assets",
            // #1011 vocabulary pass: "Devices" everywhere a human reads it.
            // The route, the table and the column key stay `assets` — this is
            // about what the office and the customer see, not a rename.
            header: "Devices",
            sortable: true,
            firstSortDir: "desc",
            render: (s) => (
                <div className="flex flex-col gap-1">
                    <span className="font-medium">
                        {s.asset_count} due
                        {s.overdue_count > 0 ? (
                            <span className="text-destructive ml-1.5 text-xs font-normal">
                                ({s.overdue_count} overdue)
                            </span>
                        ) : null}
                    </span>
                    {s.asset_types.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                            {s.asset_types.map((t) => (
                                <Badge key={t} variant="secondary" className="text-xs">
                                    {labelForAssetType(t)}
                                </Badge>
                            ))}
                        </div>
                    ) : null}
                </div>
            ),
        },
        {
            key: "provider",
            header: "Water provider",
            sortable: true,
            render: (s) =>
                s.water_provider_name ? (
                    <span className="text-sm">{s.water_provider_name}</span>
                ) : (
                    <span className="text-xs text-foreground/80">—</span>
                ),
        },
        {
            key: "actions",
            header: "",
            className: "w-1",
            render: (s) =>
                s.queued_visit_job_id ? (
                    <Link
                        to={`/jobs/${s.queued_visit_job_id}`}
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        title="A visit for this site already exists in the dispatch backlog (queued = created, waiting to be put on the calendar) — click to open it"
                    >
                        <CheckCircle2 className="h-4 w-4" />
                        Already queued
                        <ChevronRight className="h-3 w-3" />
                    </Link>
                ) : (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                            // Don't trigger any row-click navigation.
                            e.stopPropagation();
                            openSchedule(s);
                        }}
                    >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Schedule visit
                    </Button>
                ),
        },
    ];

    // ── Render ──────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground flex items-center gap-2">
                    <CalendarClock className="h-6 w-6 text-primary" />
                    Test-due roster
                </h1>
                <p className="text-shell-foreground/80 text-sm">
                    Sites with assets coming due. Queue a visit to drop the site into the dispatch backlog.
                </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard title="Sites in window" value={totalSites} icon={<CalendarClock className="h-4 w-4" />} />
                <StatCard
                    title="Overdue (this page)"
                    value={overdueSites}
                    icon={<AlertTriangle className="h-4 w-4" />}
                    className={overdueSites > 0 ? "border-destructive/30" : undefined}
                />
                <StatCard title="Due now (this page)" value={dueNowSites} icon={<AlertCircle className="h-4 w-4" />} />
                <StatCard
                    title="Assets on this page"
                    value={totalAssetsOnPage}
                    icon={<CalendarClock className="h-4 w-4" />}
                />
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Filters</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <label className="text-xs font-medium text-foreground/80">Window</label>
                        <Select
                            value={String(daysAhead)}
                            onChange={(e) => {
                                setDaysAhead(parseInt(e.target.value, 10));
                                setPage(1);
                            }}
                        >
                            {DAYS_AHEAD_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-foreground/80">Asset type</label>
                        <Select
                            value={assetType}
                            onChange={(e) => {
                                setAssetType(e.target.value);
                                setPage(1);
                            }}
                        >
                            {ASSET_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <label className="flex items-center gap-2 text-sm self-end">
                        <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={includeOverdue}
                            onChange={(e) => {
                                setIncludeOverdue(e.target.checked);
                                setPage(1);
                            }}
                        />
                        Include overdue
                    </label>
                    <label className="flex items-center gap-2 text-sm self-end">
                        <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={hideAlreadyQueued}
                            onChange={(e) => {
                                setHideAlreadyQueued(e.target.checked);
                                setPage(1);
                            }}
                        />
                        Hide sites with a queued visit
                    </label>
                </CardContent>
            </Card>

            {/* Desktop: dense table. Hidden on phones where it overflows. */}
            <div className="hidden md:block">
                <DataTable
                    columns={columns}
                    data={sites}
                    keyField="site_id"
                    loading={roster.isLoading || !sort.loaded}
                    controlledSort={{
                        key: sort.sortKey,
                        dir: sort.sortDir,
                        onChange: (key, firstDir) => {
                            sort.toggleSort(key, firstDir);
                            setPage(1);
                        },
                    }}
                    emptyMessage={
                        roster.isError
                            ? "Could not load roster. Try again in a moment."
                            : "No devices are due in this period"
                    }
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                />
            </div>

            {/* Mobile: stacked cards so nothing clips on a 390px screen. */}
            <div className="md:hidden space-y-3">
                {roster.isLoading || !sort.loaded ? (
                    <div className="rounded-lg border bg-card p-8 text-center text-sm text-foreground/80">
                        <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                        Loading roster…
                    </div>
                ) : sites.length === 0 ? (
                    <div className="rounded-lg border bg-card p-8 text-center text-sm text-foreground/80">
                        {roster.isError
                            ? "Could not load roster. Try again in a moment."
                            : "Nothing due in this window — well played."}
                    </div>
                ) : (
                    sites.map((s) => {
                        const style = bucketStyle(s.due_bucket);
                        const addr = formatAddressOneLine(s);
                        return (
                            <div key={s.site_id} className="space-y-3 rounded-lg border bg-card p-4 shadow-card">
                                <div className="flex items-start justify-between gap-2">
                                    <Link
                                        to={`/customers/${s.customer_id}`}
                                        className="text-base font-bold leading-tight hover:underline"
                                    >
                                        {s.customer_name}
                                    </Link>
                                    <Badge variant="outline" className={cn("shrink-0 text-xs", style.badge)}>
                                        {style.label}
                                    </Badge>
                                </div>
                                <div className="space-y-1 text-sm">
                                    <div className="font-medium">{s.site_name ?? "—"}</div>
                                    {addr ? (
                                        <div className="flex items-center gap-1 text-xs text-foreground/80">
                                            <MapPin className="h-3 w-3 shrink-0" />
                                            {addr}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground/80">
                                    <span className="font-medium text-foreground">
                                        {s.asset_count} due
                                        {s.overdue_count > 0 ? (
                                            <span className="ml-1 text-destructive">({s.overdue_count} overdue)</span>
                                        ) : null}
                                    </span>
                                    <span aria-hidden>•</span>
                                    <span>{s.water_provider_name ?? "No water provider"}</span>
                                </div>
                                <div className="text-xs text-foreground/80">
                                    {daysUntilLabel(s.earliest_due_date, today)}
                                </div>
                                {s.queued_visit_job_id ? (
                                    <Link
                                        to={`/jobs/${s.queued_visit_job_id}`}
                                        className="flex min-h-11 w-full items-center justify-center gap-1 rounded-md border border-border text-sm font-medium text-primary"
                                        title="A visit for this site already exists in the dispatch backlog (queued = created, waiting to be put on the calendar) — tap to open it"
                                    >
                                        <CheckCircle2 className="h-4 w-4" />
                                        Already queued
                                        <ChevronRight className="h-3 w-3" />
                                    </Link>
                                ) : (
                                    <Button
                                        variant="outline"
                                        className="min-h-11 w-full"
                                        onClick={() => openSchedule(s)}
                                    >
                                        <Plus className="mr-1 h-4 w-4" />
                                        Schedule visit
                                    </Button>
                                )}
                            </div>
                        );
                    })
                )}
                {totalPages > 1 ? (
                    <div className="flex items-center justify-between pt-1">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            Previous
                        </Button>
                        <span className="text-xs text-foreground/80">
                            Page {page} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            Next
                        </Button>
                    </div>
                ) : null}
            </div>

            <Dialog
                open={!!visitTarget}
                onClose={closeSchedule}
                title={visitTarget ? `Queue visit at ${visitTarget.site_name ?? visitTarget.customer_name}` : ""}
            >
                {visitTarget ? (
                    <div className="space-y-4">
                        <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
                            <div>
                                <span className="font-medium">Customer: </span>
                                {visitTarget.customer_name}
                            </div>
                            <div>
                                <span className="font-medium">Assets due: </span>
                                {visitTarget.asset_count}
                                {visitTarget.overdue_count > 0 ? ` (${visitTarget.overdue_count} overdue)` : ""}
                            </div>
                            <div className="text-xs text-foreground/80">
                                The visit will bundle every active asset at this site whose due date falls within{" "}
                                {daysAhead} days. Dispatch can adjust afterward.
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">Target date (optional)</label>
                            <Input
                                type="date"
                                value={visitTargetDate}
                                onChange={(e) => setVisitTargetDate(e.target.value)}
                            />
                            <p className="text-xs text-foreground/80">
                                A soft deadline for dispatch. Doesn't book a timeslot.
                            </p>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">Notes for dispatch (optional)</label>
                            <Textarea
                                rows={3}
                                value={visitNotes}
                                onChange={(e) => setVisitNotes(e.target.value)}
                                placeholder="Locked gate code, who to call, parking quirks…"
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={closeSchedule} disabled={visitMutation.isPending}>
                                Cancel
                            </Button>
                            <Button onClick={submitVisit} disabled={visitMutation.isPending}>
                                {visitMutation.isPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Plus className="mr-2 h-4 w-4" />
                                )}
                                Queue visit
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Dialog>
        </div>
    );
}

function extractErrorMessage(err: ApiError): string {
    const body = err.body as Record<string, unknown> | undefined;
    if (!body) return err.message || "Request failed.";
    if (typeof body.message === "string") return body.message;
    if (typeof body.error === "string") {
        switch (body.error) {
            case "SITE_NOT_FOUND":
                return "Site no longer exists. Refresh the roster.";
            case "NO_DUE_ASSETS":
                return "No active assets at this site fall within the bundle window.";
            case "ASSET_SITE_MISMATCH":
                return "One or more assets aren't at this site anymore.";
            case "USER_NOT_FOUND":
                return "Assigned tech isn't active.";
            case "VALIDATION":
                return "The form data was rejected by the server.";
            default:
                return body.error;
        }
    }
    return err.message || "Request failed.";
}
